<template>
  <div class="assign-landlord-users-container">
    <BaseItemsList
      v-for="(landlordKey, index) in [
        'assignedLandlord',
        'secondaryAssignedLandlord'
      ]"
      :key="landlordKey"
      :class="{ 'no-pointer-events': hasRemovalTypeStatus }"
      :items="filteredUsers"
      :show-contact="true"
      @click="assignLandlordUser($event, index === 1)"
    >
      <BaseIconText
        v-if="!getAssignedUserId(landlordKey)"
        :icon-url="svgs.ICONS.ADMINISTRATOR"
        size="larger"
        class="assign-button"
      >
        {{
          hasRemovalTypeStatus
            ? 'Ej tilldelad'
            : `Tilldela handläggare ${index + 1}`
        }}
      </BaseIconText>

      <UserIcon
        v-else
        :first-name="getAssignedFirstName(landlordKey)"
        :last-name="getAssignedLastName(landlordKey)"
        :full-name-visible="true"
      />
    </BaseItemsList>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import svgs from '@/utils/icon-constants';
import {
  assignLandlordUser,
  unassignLandlordUser,
  assignSecondaryLandlordUser,
  unassignSecondaryLandlordUser
} from '@/services/appService';
import statusTypes from '@/utils/statusTypes';

import BaseIconText from '@/components/BaseIconText.vue';
import BaseItemsList from '@/components/BaseItemsList.vue';
import UserIcon from '@/components/UserIcon.vue';

export default {
  name: 'AhOvAssignUser',
  components: {
    BaseIconText,
    BaseItemsList,
    UserIcon
  },
  props: {
    application: {
      type: Object,
      required: true
    }
  },
  emits: ['updateApplication'],

  data() {
    return {
      svgs,
      statusTypes
    };
  },
  computed: {
    ...mapState({
      landlordUsersWithUnAssign: state => state.applications.landlordUsers
    }),
    filteredUsers() {
      return this.landlordUsersWithUnAssign.filter(user => {
        const userActive = !user.inactivatedAt;
        let matchingLandlordId = true;
        // Don't filter "ej tilldelad" which has no landlordid
        if (Object.prototype.hasOwnProperty.call(user, 'landlordId')) {
          matchingLandlordId = user.landlordId === this.application?.landlordId;
        }
        return userActive && matchingLandlordId;
      });
    },
    applicationId() {
      return this.application.id;
    },
    applicationType() {
      return this.application.applicationType;
    },
    isRemoved() {
      return this.application.landlordStatus === this.statusTypes.REMOVED;
    },
    isPendingRemoval() {
      return (
        this.application.landlordStatus === this.statusTypes.PENDING_REMOVE
      );
    },
    hasRemovalTypeStatus() {
      return this.isRemoved || this.isPendingRemoval;
    }
  },
  methods: {
    ...mapMutations({
      setAhApplication: 'applications/setAhApplication',
      setOvApplication: 'applications/setOvApplication'
    }),
    getManagerKey(landlordKey) {
      return landlordKey.replace('Landlord', 'Manager');
    },
    getAssignedUserId(landlordKey) {
      const managerKey = this.getManagerKey(landlordKey);
      return (
        this.application[`${managerKey}UserId`] ||
        this.application[`${landlordKey}UserId`]
      );
    },
    getAssignedFirstName(landlordKey) {
      const managerKey = this.getManagerKey(landlordKey);
      if (this.application[`${managerKey}UserId`]) {
        return this.application[`${managerKey}FirstName`];
      }
      return this.application[`${landlordKey}FirstName`];
    },
    getAssignedLastName(landlordKey) {
      const managerKey = this.getManagerKey(landlordKey);
      if (this.application[`${managerKey}UserId`]) {
        return this.application[`${managerKey}LastName`];
      }
      return this.application[`${landlordKey}LastName`];
    },
    updateAppState(prefix, payload) {
      const data = {
        id: this.applicationId,
        [`${prefix}UserId`]: payload.id,
        [`${prefix}FirstName`]: payload.firstName,
        [`${prefix}LastName`]: payload.lastName
      };

      this.$emit('updateApplication', {
        [`${prefix}UserId`]: payload.id,
        [`${prefix}FirstName`]: payload.firstName,
        [`${prefix}LastName`]: payload.lastName
      });

      if (this.applicationType === 'andrahand') {
        this.setAhApplication(data);
      } else {
        this.setOvApplication(data);
      }
    },
    async assignLandlordUser(user, secondary = false) {
      const { firstName, id, lastName, isManagerUser } = user;
      const prefix = secondary
        ? 'secondaryAssignedLandlord'
        : 'assignedLandlord';
      const assignFn = secondary
        ? assignSecondaryLandlordUser
        : assignLandlordUser;

      if (id === null) {
        return this.unAssignLandlordUser(secondary);
      }

      const payload = { id, firstName, lastName };

      await assignFn(
        this.applicationId,
        id,
        this.applicationType,
        isManagerUser
      );
      this.updateAppState(prefix, payload);
    },
    async unAssignLandlordUser(secondary = false) {
      const prefix = secondary
        ? 'secondaryAssignedLandlord'
        : 'assignedLandlord';
      const unAssignFn = secondary
        ? unassignSecondaryLandlordUser
        : unassignLandlordUser;

      await unAssignFn(this.applicationId, this.applicationType);
      this.updateAppState(prefix, {
        id: null,
        firstName: null,
        lastName: null
      });
    }
  }
};
</script>

<style scoped>
.assign-landlord-users-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
</style>
